/**
 * Brain Deep Dive - Advanced Visualization Components
 * Shows: Calculations, Function Graph, Data Flow, Variable Dependencies
 * + Tones, Industries, Slop Blacklist, Token Limits
 *
 * @version 2.0.0 - 100% TRANSPARENCY UPDATE
 * @date 2026-01-26
 */

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator,
  GitBranch,
  Workflow,
  Network,
  ChevronRight,
  ChevronDown,
  Zap,
  Code,
  ArrowRight,
  Info,
  Play,
  Mic,
  Building2,
  Ban,
  Gauge,
  ArrowLeft,
  Search,
  X
} from 'lucide-react';
import {
  CALCULATION_DETAILS,
  FUNCTION_CALL_GRAPH,
  DATA_FLOW_STAGES,
  VARIABLE_DEPENDENCIES,
  TONE_DEFINITIONS,
  INDUSTRY_CONTEXTS,
  AI_SLOP_BLACKLIST,
  TOKEN_LIMITS
} from '../../data/brainModules';

// ============================================================================
// CALCULATION VISUALIZER - Interactive formulas
// ============================================================================
const CalculationVisualizer = () => {
  const [activeCalc, setActiveCalc] = useState('contextScore');
  const [hoveredInput, setHoveredInput] = useState(null);

  const calculations = Object.entries(CALCULATION_DETAILS);

  const getCalcColor = (key) => {
    const colors = {
      contextScore: '#10B981',
      bracketLevel: '#8B5CF6',
      responseLength: '#F59E0B',
      maxTokens: '#3B82F6',
      sentimentHint: '#EC4899',
      includeEmojis: '#6366F1',
      ambiguityAnalysis: '#EF4444',
      entityExtraction: '#14B8A6'
    };
    return colors[key] || '#6B7280';
  };

  const activeData = CALCULATION_DETAILS[activeCalc];

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Calculator size={24} color="#10B981" />
        <h2 style={styles.sectionTitle}>How It Calculates</h2>
      </div>

      {/* Calculation Selector */}
      <div style={styles.calcTabs}>
        {calculations.map(([key, data]) => (
          <button
            key={key}
            onClick={() => setActiveCalc(key)}
            style={{
              ...styles.calcTab,
              backgroundColor: activeCalc === key ? getCalcColor(key) + '30' : 'transparent',
              borderColor: activeCalc === key ? getCalcColor(key) : '#374151',
              color: activeCalc === key ? getCalcColor(key) : '#9CA3AF'
            }}
          >
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </button>
        ))}
      </div>

      {/* Active Calculation Display */}
      {activeData && (
        <div style={styles.calcDisplay}>
          {/* Function Name */}
          <div style={styles.functionName}>
            <Code size={16} color={getCalcColor(activeCalc)} />
            <code style={{ color: getCalcColor(activeCalc) }}>{activeData.function}</code>
          </div>

          {/* Formula */}
          <div style={styles.formulaBox}>
            <span style={styles.formulaLabel}>FORMULA</span>
            <div style={styles.formula}>{activeData.formula}</div>
          </div>

          {/* Inputs */}
          {activeData.inputs && (
            <div style={styles.inputsSection}>
              <span style={styles.sectionLabel}>INPUTS</span>
              <div style={styles.inputGrid}>
                {Object.entries(activeData.inputs).map(([inputKey, inputData]) => (
                  <div
                    key={inputKey}
                    style={{
                      ...styles.inputCard,
                      borderColor: hoveredInput === inputKey ? getCalcColor(activeCalc) : '#374151',
                      transform: hoveredInput === inputKey ? 'scale(1.02)' : 'scale(1)'
                    }}
                    onMouseEnter={() => setHoveredInput(inputKey)}
                    onMouseLeave={() => setHoveredInput(null)}
                  >
                    <div style={styles.inputHeader}>
                      <span style={styles.inputName}>{inputKey}</span>
                      {inputData.points && (
                        <span style={{...styles.pointsBadge, backgroundColor: getCalcColor(activeCalc) + '30', color: getCalcColor(activeCalc)}}>
                          +{inputData.points} pts
                        </span>
                      )}
                    </div>
                    <div style={styles.inputCondition}>
                      {inputData.condition || inputData.type || inputData.range || JSON.stringify(inputData.contains)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outputs / Modes */}
          {activeData.modes && (
            <div style={styles.modesSection}>
              <span style={styles.sectionLabel}>MODES</span>
              <div style={styles.modesList}>
                {Object.entries(activeData.modes).map(([modeKey, modeDesc]) => (
                  <div key={modeKey} style={styles.modeItem}>
                    <Zap size={14} color={getCalcColor(activeCalc)} />
                    <span style={styles.modeName}>{modeKey}:</span>
                    <span style={styles.modeDesc}>{modeDesc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeData.outputs && (
            <div style={styles.outputSection}>
              <span style={styles.sectionLabel}>OUTPUT</span>
              <div style={styles.outputBox}>
                {Object.entries(activeData.outputs).map(([outKey, outVal]) => (
                  <div key={outKey} style={styles.outputItem}>
                    <ArrowRight size={14} color="#10B981" />
                    <span>{outKey}: {outVal}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// FUNCTION CALL GRAPH - Which functions trigger which
// ============================================================================
const FunctionCallGraph = () => {
  const [expandedStep, setExpandedStep] = useState(1);

  if (!FUNCTION_CALL_GRAPH) return null;

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <GitBranch size={24} color="#8B5CF6" />
        <h2 style={styles.sectionTitle}>Function Call Graph</h2>
      </div>

      {/* Entry Point */}
      <div style={styles.entryPoint}>
        <Play size={16} color="#10B981" />
        <code style={styles.entryCode}>{FUNCTION_CALL_GRAPH.entryPoint}</code>
        <span style={styles.entryLabel}>Entry Point</span>
      </div>

      {/* Call Sequence */}
      <div style={styles.callSequence}>
        {FUNCTION_CALL_GRAPH.callSequence?.map((step, index) => (
          <div key={step.step} style={styles.sequenceStep}>
            {/* Step Header */}
            <div
              style={{
                ...styles.stepHeader,
                backgroundColor: expandedStep === step.step ? '#8B5CF620' : 'transparent'
              }}
              onClick={() => setExpandedStep(expandedStep === step.step ? null : step.step)}
            >
              <div style={styles.stepNumber}>{step.step}</div>
              <div style={styles.stepInfo}>
                <span style={styles.stepFunction}>{step.function}</span>
                <span style={styles.stepPurpose}>{step.purpose}</span>
              </div>
              {expandedStep === step.step ?
                <ChevronDown size={20} color="#8B5CF6" /> :
                <ChevronRight size={20} color="#6B7280" />
              }
            </div>

            {/* Expanded: Show called functions */}
            {expandedStep === step.step && step.calls && (
              <div style={styles.calledFunctions}>
                {step.calls.map((fn, fnIndex) => (
                  <div key={fn} style={styles.calledFunction}>
                    <div style={styles.callConnector}>
                      {fnIndex < step.calls.length - 1 && <div style={styles.connectorLine} />}
                    </div>
                    <Code size={14} color="#8B5CF6" />
                    <code style={styles.fnName}>{fn}()</code>
                  </div>
                ))}
              </div>
            )}

            {/* Returns */}
            {step.returns && expandedStep === step.step && (
              <div style={styles.returnsBox}>
                <ArrowRight size={14} color="#10B981" />
                <span style={styles.returnsLabel}>Returns:</span>
                <code style={styles.returnsCode}>{step.returns}</code>
              </div>
            )}

            {/* Connector to next step */}
            {index < FUNCTION_CALL_GRAPH.callSequence.length - 1 && (
              <div style={styles.stepConnector}>
                <div style={styles.stepConnectorLine} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// DATA FLOW PIPELINE - Animated stages
// ============================================================================
const DataFlowPipeline = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  if (!DATA_FLOW_STAGES) return null;

  const stageColors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'];

  const runAnimation = () => {
    setIsAnimating(true);
    setActiveStage(0);

    DATA_FLOW_STAGES.forEach((_, index) => {
      setTimeout(() => {
        setActiveStage(index);
        if (index === DATA_FLOW_STAGES.length - 1) {
          setTimeout(() => setIsAnimating(false), 1000);
        }
      }, index * 800);
    });
  };

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Workflow size={24} color="#F59E0B" />
        <h2 style={styles.sectionTitle}>Data Flow Pipeline</h2>
        <button onClick={runAnimation} style={styles.animateButton} disabled={isAnimating}>
          <Play size={16} />
          {isAnimating ? 'Running...' : 'Animate'}
        </button>
      </div>

      {/* Pipeline Visualization */}
      <div style={styles.pipeline}>
        {DATA_FLOW_STAGES.map((stage, index) => (
          <React.Fragment key={stage.stage}>
            {/* Stage Box */}
            <div
              style={{
                ...styles.stageBox,
                borderColor: activeStage >= index ? stageColors[index % 5] : '#374151',
                backgroundColor: activeStage >= index ? stageColors[index % 5] + '15' : '#111827',
                transform: activeStage === index && isAnimating ? 'scale(1.05)' : 'scale(1)',
                boxShadow: activeStage === index && isAnimating ? `0 0 30px ${stageColors[index % 5]}40` : 'none'
              }}
              onClick={() => setActiveStage(index)}
            >
              <div style={{...styles.stageHeader, color: stageColors[index % 5]}}>
                {stage.stage}
              </div>
              <div style={styles.stageDesc}>{stage.description}</div>

              {/* Stage Data Preview */}
              {activeStage === index && (
                <div style={styles.stageData}>
                  {stage.data && Object.keys(stage.data).slice(0, 3).map(key => (
                    <div key={key} style={styles.stageDataItem}>
                      <span style={styles.dataKey}>{key}</span>
                    </div>
                  ))}
                  {stage.transformations && Object.keys(stage.transformations).slice(0, 3).map(key => (
                    <div key={key} style={styles.stageDataItem}>
                      <span style={styles.dataKey}>{key}</span>
                    </div>
                  ))}
                  {stage.sections && Object.keys(stage.sections).slice(0, 3).map(key => (
                    <div key={key} style={styles.stageDataItem}>
                      <span style={styles.dataKey}>&lt;{key}&gt;</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Arrow between stages */}
            {index < DATA_FLOW_STAGES.length - 1 && (
              <div style={{
                ...styles.pipelineArrow,
                color: activeStage > index ? stageColors[index % 5] : '#374151'
              }}>
                <ArrowRight size={24} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Active Stage Details */}
      {DATA_FLOW_STAGES[activeStage] && (
        <div style={styles.stageDetails}>
          <h3 style={{...styles.stageDetailTitle, color: stageColors[activeStage % 5]}}>
            {DATA_FLOW_STAGES[activeStage].stage}: {DATA_FLOW_STAGES[activeStage].description}
          </h3>
          <div style={styles.stageDetailContent}>
            {DATA_FLOW_STAGES[activeStage].data && (
              <div style={styles.detailSection}>
                <span style={styles.detailLabel}>Data:</span>
                {Object.entries(DATA_FLOW_STAGES[activeStage].data).map(([key, val]) => (
                  <div key={key} style={styles.detailItem}>
                    <code style={styles.detailKey}>{key}</code>
                    <span style={styles.detailVal}>{typeof val === 'string' ? val : JSON.stringify(val)}</span>
                  </div>
                ))}
              </div>
            )}
            {DATA_FLOW_STAGES[activeStage].transformations && (
              <div style={styles.detailSection}>
                <span style={styles.detailLabel}>Transformations:</span>
                {Object.entries(DATA_FLOW_STAGES[activeStage].transformations).map(([key, val]) => (
                  <div key={key} style={styles.detailItem}>
                    <code style={styles.detailKey}>{key}</code>
                    <span style={styles.detailVal}>{val}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// VARIABLE DEPENDENCIES - What feeds into what
// ============================================================================
const VariableDependencies = () => {
  const [selectedVar, setSelectedVar] = useState(null);

  if (!VARIABLE_DEPENDENCIES) return null;

  const { primaryInputs, intermediates, finalOutputs } = VARIABLE_DEPENDENCIES;

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Network size={24} color="#EC4899" />
        <h2 style={styles.sectionTitle}>Variable Dependencies</h2>
      </div>

      <div style={styles.depContainer}>
        {/* Primary Inputs */}
        <div style={styles.depColumn}>
          <div style={styles.depColumnHeader}>
            <div style={{...styles.depDot, backgroundColor: '#10B981'}} />
            <span>Primary Inputs</span>
          </div>
          <div style={styles.depList}>
            {primaryInputs?.map(input => (
              <div
                key={input}
                style={{
                  ...styles.depItem,
                  backgroundColor: selectedVar === input ? '#10B98130' : 'transparent',
                  borderColor: selectedVar === input ? '#10B981' : '#374151'
                }}
                onClick={() => setSelectedVar(selectedVar === input ? null : input)}
              >
                <code>{input}</code>
              </div>
            ))}
          </div>
        </div>

        {/* Arrow */}
        <div style={styles.depArrow}>
          <ArrowRight size={32} color="#374151" />
        </div>

        {/* Intermediates */}
        <div style={styles.depColumn}>
          <div style={styles.depColumnHeader}>
            <div style={{...styles.depDot, backgroundColor: '#F59E0B'}} />
            <span>Intermediates</span>
          </div>
          <div style={styles.depList}>
            {intermediates && Object.entries(intermediates).map(([key, data]) => (
              <div
                key={key}
                style={{
                  ...styles.depItem,
                  ...styles.depItemIntermediate,
                  backgroundColor: selectedVar === key ? '#F59E0B30' : 'transparent',
                  borderColor: selectedVar === key ? '#F59E0B' : '#374151'
                }}
                onClick={() => setSelectedVar(selectedVar === key ? null : key)}
              >
                <code style={styles.depItemName}>{key}</code>
                {selectedVar === key && (
                  <div style={styles.depItemDetails}>
                    <div style={styles.depFlow}>
                      <span style={styles.depFlowLabel}>Depends on:</span>
                      {data.dependsOn?.map(dep => (
                        <span key={dep} style={styles.depTag}>{dep}</span>
                      ))}
                    </div>
                    <div style={styles.depFlow}>
                      <span style={styles.depFlowLabel}>Feeds into:</span>
                      {data.feedsInto?.map(feed => (
                        <span key={feed} style={{...styles.depTag, backgroundColor: '#8B5CF630'}}>{feed}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Arrow */}
        <div style={styles.depArrow}>
          <ArrowRight size={32} color="#374151" />
        </div>

        {/* Final Outputs */}
        <div style={styles.depColumn}>
          <div style={styles.depColumnHeader}>
            <div style={{...styles.depDot, backgroundColor: '#8B5CF6'}} />
            <span>Final Outputs</span>
          </div>
          <div style={styles.depList}>
            {finalOutputs && Object.entries(finalOutputs).map(([key, data]) => (
              <div
                key={key}
                style={{
                  ...styles.depItem,
                  backgroundColor: selectedVar === key ? '#8B5CF630' : 'transparent',
                  borderColor: selectedVar === key ? '#8B5CF6' : '#374151'
                }}
                onClick={() => setSelectedVar(selectedVar === key ? null : key)}
              >
                <code style={styles.depItemName}>{key}</code>
                {selectedVar === key && data.assembledFrom && (
                  <div style={styles.depItemDetails}>
                    <span style={styles.depFlowLabel}>Assembled from:</span>
                    <div style={styles.assembledList}>
                      {data.assembledFrom.map(part => (
                        <span key={part} style={styles.depTag}>{part}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TONE EXPLORER - Interactive tone visualization
// ============================================================================
const ToneExplorer = () => {
  const [activeTone, setActiveTone] = useState('friendly');
  const tones = Object.entries(TONE_DEFINITIONS);

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Mic size={24} color="#10B981" />
        <h2 style={styles.sectionTitle}>Voice & Tone System</h2>
        <span style={{ color: '#6B7280', fontSize: '14px' }}>{tones.length} tones available</span>
      </div>

      {/* Tone Selector */}
      <div style={styles.toneGrid}>
        {tones.map(([key, tone]) => (
          <div
            key={key}
            onClick={() => setActiveTone(key)}
            style={{
              ...styles.toneCard,
              borderColor: activeTone === key ? tone.color : '#374151',
              backgroundColor: activeTone === key ? tone.color + '15' : '#1F2937'
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>{tone.icon}</div>
            <div style={{ color: 'white', fontWeight: 600, marginBottom: '4px' }}>{tone.name}</div>
            <div style={{ color: '#9CA3AF', fontSize: '12px', textAlign: 'center' }}>{tone.description}</div>
          </div>
        ))}
      </div>

      {/* Active Tone Details */}
      {TONE_DEFINITIONS[activeTone] && (
        <div style={{ ...styles.toneDetails, borderColor: TONE_DEFINITIONS[activeTone].color }}>
          <div style={styles.toneSection}>
            <span style={styles.toneSectionLabel}>BEST FOR</span>
            <div style={styles.tagList}>
              {TONE_DEFINITIONS[activeTone].bestFor?.map(item => (
                <span key={item} style={{ ...styles.toneTag, backgroundColor: TONE_DEFINITIONS[activeTone].color + '30', color: TONE_DEFINITIONS[activeTone].color }}>{item}</span>
              ))}
            </div>
          </div>

          <div style={styles.toneSection}>
            <span style={styles.toneSectionLabel}>EMOJI GUIDANCE</span>
            <div style={styles.emojiBox}>
              <div style={styles.emojiRow}>
                <span style={{ color: '#10B981' }}>When enabled:</span>
                <span style={{ color: '#E5E7EB' }}>{TONE_DEFINITIONS[activeTone].emojiGuidance?.enabled}</span>
              </div>
              <div style={styles.emojiRow}>
                <span style={{ color: '#EF4444' }}>When disabled:</span>
                <span style={{ color: '#E5E7EB' }}>{TONE_DEFINITIONS[activeTone].emojiGuidance?.disabled}</span>
              </div>
            </div>
          </div>

          <div style={styles.toneSection}>
            <span style={styles.toneSectionLabel}>EXAMPLE</span>
            <div style={styles.exampleBox}>
              <div style={styles.exampleReview}>
                <span style={{ color: '#6B7280', fontSize: '11px' }}>REVIEW</span>
                <p style={{ color: '#E5E7EB', margin: '4px 0 0', fontSize: '14px' }}>"{TONE_DEFINITIONS[activeTone].example?.review}"</p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                <ArrowRight color="#374151" />
              </div>
              <div style={{ ...styles.exampleResponse, borderColor: TONE_DEFINITIONS[activeTone].color }}>
                <span style={{ color: TONE_DEFINITIONS[activeTone].color, fontSize: '11px' }}>RESPONSE</span>
                <p style={{ color: '#E5E7EB', margin: '4px 0 0', fontSize: '14px' }}>"{TONE_DEFINITIONS[activeTone].example?.response}"</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// INDUSTRY EXPLORER - All 19 industries
// ============================================================================
const IndustryExplorer = () => {
  const [activeIndustry, setActiveIndustry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const industries = Object.entries(INDUSTRY_CONTEXTS);

  const filteredIndustries = industries.filter(([key, ind]) =>
    ind.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ind.context.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Building2 size={24} color="#3B82F6" />
        <h2 style={styles.sectionTitle}>Industry Contexts</h2>
        <span style={{ color: '#6B7280', fontSize: '14px' }}>{industries.length} industries</span>
      </div>

      {/* Search */}
      <div style={styles.searchBox}>
        <Search size={18} color="#6B7280" />
        <input
          type="text"
          placeholder="Search industries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} style={styles.clearBtn}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* Industry Grid */}
      <div style={styles.industryGrid}>
        {filteredIndustries.map(([key, industry]) => (
          <div
            key={key}
            onClick={() => setActiveIndustry(activeIndustry === key ? null : key)}
            style={{
              ...styles.industryCard,
              borderColor: activeIndustry === key ? industry.color : '#374151',
              backgroundColor: activeIndustry === key ? industry.color + '10' : 'transparent'
            }}
          >
            <div style={styles.industryHeader}>
              <span style={{ fontSize: '24px' }}>{industry.icon}</span>
              <span style={{ color: 'white', fontWeight: 500 }}>{industry.name}</span>
            </div>
            <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '8px 0 0', lineHeight: 1.4 }}>{industry.context}</p>

            {activeIndustry === key && (
              <div style={styles.industryDetails}>
                <div style={styles.industrySection}>
                  <span style={styles.industrySectionLabel}>VOICE</span>
                  <p style={{ color: '#E5E7EB', fontSize: '13px', margin: 0, fontStyle: 'italic' }}>"{industry.voice}"</p>
                </div>
                <div style={styles.industrySection}>
                  <span style={styles.industrySectionLabel}>KEY DETAILS</span>
                  <div style={styles.tagList}>
                    {industry.details?.map(d => (
                      <span key={d} style={{ ...styles.industryTag, backgroundColor: industry.color + '20', color: industry.color }}>{d}</span>
                    ))}
                  </div>
                </div>
                <div style={styles.industrySection}>
                  <span style={styles.industrySectionLabel}>KEYWORDS</span>
                  <div style={styles.tagList}>
                    {industry.keywords?.map(k => (
                      <span key={k} style={styles.keywordTag}>{k}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// SLOP BLACKLIST - Forbidden phrases
// ============================================================================
const SlopBlacklist = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [showWords, setShowWords] = useState(true);

  const allItems = [
    ...AI_SLOP_BLACKLIST.phrases.items.map(i => ({ ...i, type: 'phrase' })),
    ...AI_SLOP_BLACKLIST.words.items.map(i => ({ ...i, type: 'word' }))
  ];

  const categories = Object.entries(AI_SLOP_BLACKLIST.categories);
  const filteredItems = activeCategory === 'all'
    ? allItems
    : allItems.filter(i => i.category === activeCategory);

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#6B7280';
      default: return '#6B7280';
    }
  };

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Ban size={24} color="#EF4444" />
        <h2 style={styles.sectionTitle}>AI Slop Blacklist</h2>
        <span style={{ color: '#6B7280', fontSize: '14px' }}>
          {AI_SLOP_BLACKLIST.phrases.items.length} phrases + {AI_SLOP_BLACKLIST.words.items.length} words
        </span>
      </div>

      <p style={{ color: '#9CA3AF', marginBottom: '20px', fontSize: '14px' }}>
        These phrases and words make responses sound robotic. The AI is trained to NEVER use them.
      </p>

      {/* Category Filter */}
      <div style={styles.categoryFilter}>
        <button
          onClick={() => setActiveCategory('all')}
          style={{
            ...styles.categoryBtn,
            backgroundColor: activeCategory === 'all' ? '#374151' : 'transparent',
            color: activeCategory === 'all' ? 'white' : '#9CA3AF'
          }}
        >
          All ({allItems.length})
        </button>
        {categories.map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            style={{
              ...styles.categoryBtn,
              backgroundColor: activeCategory === key ? cat.color + '30' : 'transparent',
              color: activeCategory === key ? cat.color : '#9CA3AF',
              borderColor: activeCategory === key ? cat.color : '#374151'
            }}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Slop Items */}
      <div style={styles.slopGrid}>
        {filteredItems.map((item, idx) => (
          <div
            key={idx}
            style={{
              ...styles.slopItem,
              borderLeftColor: getSeverityColor(item.severity)
            }}
          >
            <div style={styles.slopHeader}>
              <span style={{
                ...styles.slopType,
                backgroundColor: item.type === 'phrase' ? '#EF444420' : '#F59E0B20',
                color: item.type === 'phrase' ? '#EF4444' : '#F59E0B'
              }}>
                {item.type}
              </span>
              <span style={{
                ...styles.slopSeverity,
                color: getSeverityColor(item.severity)
              }}>
                {item.severity}
              </span>
            </div>
            <div style={styles.slopText}>"{item.text}"</div>
            <div style={styles.slopCategory}>
              {AI_SLOP_BLACKLIST.categories[item.category]?.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// TOKEN LIMITS VISUALIZER
// ============================================================================
const TokenLimitsVisualizer = () => {
  const [activeLength, setActiveLength] = useState('medium');
  const lengths = ['short', 'medium', 'detailed', 'comprehensive'];

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <Gauge size={24} color="#3B82F6" />
        <h2 style={styles.sectionTitle}>Token Limits & Response Length</h2>
      </div>

      <p style={{ color: '#9CA3AF', marginBottom: '24px', fontSize: '14px' }}>
        Response length is dynamically calculated based on review complexity. Critical issues get more tokens.
      </p>

      {/* Visual Token Bars */}
      <div style={styles.tokenBars}>
        {lengths.map(length => {
          const config = TOKEN_LIMITS[length];
          const maxWidth = (config.max / 500) * 100;
          const targetWidth = (config.target / 500) * 100;

          return (
            <div
              key={length}
              onClick={() => setActiveLength(length)}
              style={{
                ...styles.tokenBarContainer,
                borderColor: activeLength === length ? config.color : '#374151'
              }}
            >
              <div style={styles.tokenBarHeader}>
                <span style={{ fontSize: '20px' }}>{config.icon}</span>
                <span style={{ color: 'white', fontWeight: 500 }}>{config.name}</span>
                <span style={{ color: '#6B7280', fontSize: '12px', marginLeft: 'auto' }}>
                  {config.min}-{config.max} tokens
                </span>
              </div>

              {/* Visual Bar */}
              <div style={styles.tokenBarBg}>
                <div style={{
                  ...styles.tokenBarFill,
                  width: `${maxWidth}%`,
                  backgroundColor: config.color + '30'
                }} />
                <div style={{
                  ...styles.tokenBarTarget,
                  left: `${targetWidth}%`,
                  backgroundColor: config.color
                }} />
              </div>

              <div style={{ color: '#9CA3AF', fontSize: '12px', marginTop: '8px' }}>
                {config.sentences}
              </div>

              {activeLength === length && (
                <div style={styles.tokenDetails}>
                  <div style={styles.tokenDetailRow}>
                    <span style={{ color: '#6B7280' }}>Use Case:</span>
                    <span style={{ color: '#E5E7EB' }}>{config.useCase}</span>
                  </div>
                  <div style={styles.tokenDetailRow}>
                    <span style={{ color: '#6B7280' }}>Example:</span>
                    <span style={{ color: '#E5E7EB', fontStyle: 'italic' }}>"{config.example}"</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bracket Multiplier Info */}
      <div style={styles.bracketMultiplierBox}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>✨</span>
          <div>
            <div style={{ color: 'white', fontWeight: 600 }}>Bracket Multiplier: {TOKEN_LIMITS.bracketMultiplier.value}x</div>
            <div style={{ color: '#9CA3AF', fontSize: '13px' }}>{TOKEN_LIMITS.bracketMultiplier.description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT - Tabs for all views
// ============================================================================
const BrainDeepDive = () => {
  const [activeTab, setActiveTab] = useState('calculations');

  const tabs = [
    { id: 'calculations', label: 'Calculations', icon: Calculator, color: '#10B981' },
    { id: 'functions', label: 'Function Graph', icon: GitBranch, color: '#8B5CF6' },
    { id: 'dataflow', label: 'Data Flow', icon: Workflow, color: '#F59E0B' },
    { id: 'dependencies', label: 'Dependencies', icon: Network, color: '#EC4899' },
    { id: 'tones', label: 'Tones', icon: Mic, color: '#14B8A6' },
    { id: 'industries', label: 'Industries', icon: Building2, color: '#3B82F6' },
    { id: 'slop', label: 'Slop Blacklist', icon: Ban, color: '#EF4444' },
    { id: 'tokens', label: 'Token Limits', icon: Gauge, color: '#6366F1' }
  ];

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <Link to="/brain" style={styles.backLink}>
            <ArrowLeft size={20} />
            <span>Back to Brain</span>
          </Link>
          <div style={styles.headerTitle}>
            <Zap color="#facc15" size={24} />
            <span>Deep Dive - 100% Transparency</span>
          </div>
        </div>
      </header>

      <div style={styles.container}>
        {/* Tab Navigation */}
        <div style={styles.tabNav}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tab,
                backgroundColor: activeTab === tab.id ? tab.color + '20' : 'transparent',
                borderColor: activeTab === tab.id ? tab.color : 'transparent',
                color: activeTab === tab.id ? tab.color : '#9CA3AF'
              }}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'calculations' && <CalculationVisualizer />}
          {activeTab === 'functions' && <FunctionCallGraph />}
          {activeTab === 'dataflow' && <DataFlowPipeline />}
          {activeTab === 'dependencies' && <VariableDependencies />}
          {activeTab === 'tones' && <ToneExplorer />}
          {activeTab === 'industries' && <IndustryExplorer />}
          {activeTab === 'slop' && <SlopBlacklist />}
          {activeTab === 'tokens' && <TokenLimitsVisualizer />}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STYLES
// ============================================================================
const styles = {
  container: {
    backgroundColor: '#0a0a0a',
    minHeight: '100%',
    padding: '24px',
  },
  tabNav: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    borderRadius: '12px',
    border: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s',
    backgroundColor: 'transparent',
  },
  tabContent: {
    minHeight: '500px',
  },
  section: {
    backgroundColor: '#111827',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #1F2937',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  sectionTitle: {
    color: 'white',
    fontSize: '20px',
    fontWeight: 600,
    margin: 0,
    flex: 1,
  },
  // Calculation Visualizer
  calcTabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '24px',
  },
  calcTab: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #374151',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    transition: 'all 0.2s',
    backgroundColor: 'transparent',
    textTransform: 'capitalize',
  },
  calcDisplay: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  functionName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#1F2937',
    borderRadius: '8px',
  },
  formulaBox: {
    padding: '16px',
    backgroundColor: '#0D1117',
    borderRadius: '12px',
    border: '1px solid #30363D',
  },
  formulaLabel: {
    display: 'block',
    color: '#6B7280',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  formula: {
    color: '#E5E7EB',
    fontSize: '16px',
    fontFamily: 'monospace',
    lineHeight: 1.6,
  },
  inputsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionLabel: {
    color: '#6B7280',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.05em',
  },
  inputGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
  },
  inputCard: {
    padding: '12px',
    backgroundColor: '#1F2937',
    borderRadius: '8px',
    border: '1px solid #374151',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  inputHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  inputName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
  },
  pointsBadge: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
  },
  inputCondition: {
    color: '#9CA3AF',
    fontSize: '12px',
    lineHeight: 1.4,
  },
  modesSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  modesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  modeItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#1F2937',
    borderRadius: '8px',
  },
  modeName: {
    color: 'white',
    fontSize: '13px',
    fontWeight: 500,
  },
  modeDesc: {
    color: '#9CA3AF',
    fontSize: '13px',
  },
  outputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  outputBox: {
    padding: '12px',
    backgroundColor: '#10B98115',
    borderRadius: '8px',
    border: '1px solid #10B98130',
  },
  outputItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#10B981',
    fontSize: '14px',
  },
  // Function Call Graph
  entryPoint: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#10B98115',
    borderRadius: '12px',
    border: '1px solid #10B98130',
    marginBottom: '24px',
  },
  entryCode: {
    color: '#10B981',
    fontSize: '16px',
    fontWeight: 500,
  },
  entryLabel: {
    color: '#6B7280',
    fontSize: '12px',
    marginLeft: 'auto',
  },
  callSequence: {
    display: 'flex',
    flexDirection: 'column',
  },
  sequenceStep: {
    position: 'relative',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#8B5CF6',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '14px',
  },
  stepInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  stepFunction: {
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
  },
  stepPurpose: {
    color: '#9CA3AF',
    fontSize: '12px',
  },
  calledFunctions: {
    marginLeft: '56px',
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  calledFunction: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'relative',
  },
  callConnector: {
    width: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  connectorLine: {
    width: '2px',
    height: '24px',
    backgroundColor: '#374151',
    position: 'absolute',
    top: '100%',
    left: '9px',
  },
  fnName: {
    color: '#C4B5FD',
    fontSize: '13px',
  },
  returnsBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: '56px',
    padding: '12px',
    backgroundColor: '#10B98115',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  returnsLabel: {
    color: '#6B7280',
    fontSize: '12px',
  },
  returnsCode: {
    color: '#10B981',
    fontSize: '12px',
  },
  stepConnector: {
    height: '24px',
    marginLeft: '32px',
    display: 'flex',
    justifyContent: 'center',
  },
  stepConnectorLine: {
    width: '2px',
    height: '100%',
    backgroundColor: '#374151',
  },
  // Data Flow Pipeline
  animateButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: '#F59E0B',
    color: 'black',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
  },
  pipeline: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '24px 0',
    overflowX: 'auto',
  },
  stageBox: {
    minWidth: '140px',
    padding: '16px',
    backgroundColor: '#111827',
    borderRadius: '12px',
    border: '2px solid #374151',
    cursor: 'pointer',
    transition: 'all 0.3s',
    textAlign: 'center',
  },
  stageHeader: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  stageDesc: {
    color: '#9CA3AF',
    fontSize: '11px',
    lineHeight: 1.4,
  },
  stageData: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  stageDataItem: {
    padding: '4px 8px',
    backgroundColor: '#1F2937',
    borderRadius: '4px',
  },
  dataKey: {
    color: '#6B7280',
    fontSize: '10px',
    fontFamily: 'monospace',
  },
  pipelineArrow: {
    flexShrink: 0,
    transition: 'color 0.3s',
  },
  stageDetails: {
    marginTop: '24px',
    padding: '20px',
    backgroundColor: '#0D1117',
    borderRadius: '12px',
    border: '1px solid #30363D',
  },
  stageDetailTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '16px',
  },
  stageDetailContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  detailSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  detailLabel: {
    color: '#6B7280',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.05em',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '8px',
    backgroundColor: '#1F2937',
    borderRadius: '6px',
  },
  detailKey: {
    color: '#A78BFA',
    fontSize: '12px',
    minWidth: '140px',
  },
  detailVal: {
    color: '#9CA3AF',
    fontSize: '12px',
    flex: 1,
  },
  // Variable Dependencies
  depContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    overflowX: 'auto',
    padding: '16px 0',
  },
  depColumn: {
    flex: 1,
    minWidth: '200px',
  },
  depColumnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
  },
  depDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
  },
  depList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  depItem: {
    padding: '10px 12px',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    border: '1px solid #374151',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  depItemIntermediate: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  depItemName: {
    color: 'white',
    fontSize: '13px',
  },
  depItemDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #374151',
  },
  depFlow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
  },
  depFlowLabel: {
    color: '#6B7280',
    fontSize: '11px',
    width: '100%',
  },
  depTag: {
    padding: '2px 8px',
    backgroundColor: '#F59E0B30',
    color: '#F59E0B',
    borderRadius: '4px',
    fontSize: '11px',
  },
  assembledList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '4px',
  },
  depArrow: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '40px',
  },
  // Page Layout
  page: {
    minHeight: '100vh',
    backgroundColor: '#030712',
    color: 'white',
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 40,
    backgroundColor: 'rgba(17, 24, 39, 0.95)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid #1f2937',
    padding: '12px 24px',
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#9CA3AF',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'color 0.2s',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '18px',
    fontWeight: 600,
  },
  // Tone Explorer
  toneGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  toneCard: {
    padding: '20px',
    borderRadius: '16px',
    border: '2px solid #374151',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  toneDetails: {
    padding: '24px',
    backgroundColor: '#0D1117',
    borderRadius: '16px',
    border: '2px solid',
  },
  toneSection: {
    marginBottom: '20px',
  },
  toneSectionLabel: {
    display: 'block',
    color: '#6B7280',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  toneTag: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
  },
  emojiBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  emojiRow: {
    display: 'flex',
    gap: '12px',
    fontSize: '13px',
  },
  exampleBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  exampleReview: {
    padding: '16px',
    backgroundColor: '#1F2937',
    borderRadius: '12px 12px 0 0',
  },
  exampleResponse: {
    padding: '16px',
    backgroundColor: '#1F2937',
    borderRadius: '0 0 12px 12px',
    borderLeft: '3px solid',
  },
  // Industry Explorer
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#1F2937',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
  },
  clearBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px',
  },
  industryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  industryCard: {
    padding: '16px',
    borderRadius: '12px',
    border: '1px solid #374151',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  industryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  industryDetails: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #374151',
  },
  industrySection: {
    marginBottom: '12px',
  },
  industrySectionLabel: {
    display: 'block',
    color: '#6B7280',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    marginBottom: '6px',
  },
  industryTag: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
  },
  keywordTag: {
    padding: '2px 8px',
    backgroundColor: '#374151',
    color: '#9CA3AF',
    borderRadius: '4px',
    fontSize: '11px',
  },
  // Slop Blacklist
  categoryFilter: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px',
  },
  categoryBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #374151',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    transition: 'all 0.2s',
    backgroundColor: 'transparent',
  },
  slopGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
  },
  slopItem: {
    padding: '12px 16px',
    backgroundColor: '#1F2937',
    borderRadius: '8px',
    borderLeft: '3px solid',
  },
  slopHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  slopType: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  slopSeverity: {
    fontSize: '11px',
    fontWeight: 500,
  },
  slopText: {
    color: '#E5E7EB',
    fontSize: '14px',
    marginBottom: '8px',
  },
  slopCategory: {
    color: '#6B7280',
    fontSize: '11px',
  },
  // Token Limits
  tokenBars: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  tokenBarContainer: {
    padding: '16px 20px',
    backgroundColor: '#1F2937',
    borderRadius: '12px',
    border: '2px solid #374151',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tokenBarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  tokenBarBg: {
    position: 'relative',
    height: '8px',
    backgroundColor: '#374151',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  tokenBarFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: '4px',
  },
  tokenBarTarget: {
    position: 'absolute',
    top: '-4px',
    width: '4px',
    height: '16px',
    borderRadius: '2px',
    transform: 'translateX(-50%)',
  },
  tokenDetails: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #374151',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  tokenDetailRow: {
    display: 'flex',
    gap: '12px',
    fontSize: '13px',
  },
  bracketMultiplierBox: {
    padding: '20px',
    backgroundColor: '#F59E0B15',
    borderRadius: '12px',
    border: '1px solid #F59E0B30',
  },
};

export default BrainDeepDive;
